import tinycolor from 'tinycolor2'

interface BackgroundAndTextColorClass {
  bg: string
  text: string
}

export const bgAndTextColors: BackgroundAndTextColorClass[] = [
  {
    bg: 'bg-navy-10',
    text: 'text-navy-80',
  },
  {
    bg: 'bg-green-10',
    text: 'text-green-80',
  },
  {
    bg: 'bg-yellow-10',
    text: 'text-yellow-80',
  },
  {
    bg: 'bg-pink-30',
    text: 'text-pink-80',
  },
  {
    bg: 'bg-purple-10',
    text: 'text-purple-80',
  },
  {
    bg: 'bg-red-10',
    text: 'text-red-80',
  },
]

export function getBackgroundAndTextColor(
  index: number,
): BackgroundAndTextColorClass {
  return bgAndTextColors[index % bgAndTextColors.length]
}

export function getBackgroundAndTextColorAsArray(index: number): string[] {
  const colors = getBackgroundAndTextColor(index)
  return [colors.bg, colors.text]
}

/**
 * Checks if a string is a valid hex color value.
 * Accepts 3-, 4-, 6-, or 8-digit hex, with or without # prefix.
 *
 * @param color - The color string to check
 * @returns true if the string is a valid hex color, false otherwise
 */
export function isHexColor(color: string): boolean {
  if (!color || typeof color !== 'string') return false

  const cleaned = color.trim().replace(/^#/, '')
  const hex3Pattern = /^[0-9a-fA-F]{3}$/
  const hex4Pattern = /^[0-9a-fA-F]{4}$/
  const hex6Pattern = /^[0-9a-fA-F]{6}$/
  const hex8Pattern = /^[0-9a-fA-F]{8}$/

  return (
    hex3Pattern.test(cleaned) ||
    hex4Pattern.test(cleaned) ||
    hex6Pattern.test(cleaned) ||
    hex8Pattern.test(cleaned)
  )
}

/**
 * Normalizes a color string to 6-digit hex with a # prefix.
 * Accepts any color format (hex, rgb, rgba, hsl, named colors, etc.) and converts to hex.
 * For hex colors: accepts 3-, 4-, 6-, or 8-digit hex, with or without # prefix.
 * Expands 3-digit hex to 6-digit (e.g., "f00" -> "#FF0000").
 * For 4-digit hex (RGBA shorthand), expands and ignores the alpha digit (e.g., "f00f" -> "#FF0000").
 * For 8-digit hex (with alpha), ignores the last 2 digits (e.g., "ff0000ff" -> "#FF0000").
 *
 * Note: Transparent colors (including 'transparent' keyword and colors with alpha=0)
 * return empty string since they cannot be represented as 6-digit hex.
 * Use isTransparent() to check for transparent colors before calling this function.
 *
 * @param color - The color string to normalize (any format)
 * @returns Normalized hex color in format #RRGGBB, or empty string if invalid/transparent
 */
export function normalizeHex(color: string | undefined): string {
  if (!color || typeof color !== 'string') return ''

  const trimmed = color.trim()

  // Handle transparent color - it's not representable as a 6-digit hex
  if (trimmed.toLowerCase() === 'transparent') return ''

  // First, try to convert any color format to hex using tinycolor
  try {
    const colorObj = tinycolor(trimmed)
    if (!colorObj.isValid()) return ''

    // Check if color is fully transparent (alpha = 0)
    // Such colors are not representable as 6-digit hex
    if (colorObj.getAlpha() === 0) return ''

    // Get hex without alpha (6 digits)
    const hex = colorObj.toHex()
    return `#${hex.toUpperCase()}`
  } catch {
    // If tinycolor fails, fall back to manual hex parsing for backwards compatibility
    const cleaned = trimmed.replace(/^#/, '')
    const hex3Pattern = /^[0-9a-fA-F]{3}$/
    const hex4Pattern = /^[0-9a-fA-F]{4}$/
    const hex6Pattern = /^[0-9a-fA-F]{6}$/
    const hex8Pattern = /^[0-9a-fA-F]{8}$/

    let normalizedHex: string | null = null

    if (hex3Pattern.test(cleaned)) {
      // Expand 3-digit hex to 6-digit (e.g., "f00" -> "FF0000")
      normalizedHex = cleaned
        .split('')
        .map(char => char + char)
        .join('')
        .toUpperCase()
    } else if (hex4Pattern.test(cleaned)) {
      // Expand 4-digit RGBA hex, then drop the alpha (e.g., "f00f" -> "FF0000")
      normalizedHex = cleaned
        .slice(0, 3)
        .split('')
        .map(char => char + char)
        .join('')
        .toUpperCase()
    } else if (hex6Pattern.test(cleaned)) {
      normalizedHex = cleaned.toUpperCase()
    } else if (hex8Pattern.test(cleaned)) {
      // Extract first 6 digits (RGB), ignore last 2 (alpha)
      normalizedHex = cleaned.slice(0, 6).toUpperCase()
    }

    return normalizedHex ? `#${normalizedHex}` : ''
  }
}

/**
 * Checks if a color string is transparent.
 * Returns true if the color is the string 'transparent' or has an alpha value of 0.
 *
 * @param color - The color string to check
 * @returns true if the color is transparent, false otherwise
 */
export function isTransparent(color: string | null | undefined): boolean {
  if (!color || typeof color !== 'string') return false
  if (color === 'transparent') return true

  try {
    const colorObj = tinycolor(color)
    return colorObj.getAlpha() === 0
  } catch {
    return false
  }
}

/**
 * Checks if a CosmeticFill is transparent.
 * Returns true if the fill is a color or paint type with a transparent color.
 * Image fills and other types are never considered transparent.
 *
 * @param fill - The CosmeticFill (or fill-like object) to check
 * @returns true if the fill is transparent, false otherwise
 */
export function isTransparentFill(
  fill:
    | { type: 'color' | 'paint'; color: string }
    | { type: 'image' }
    | { type: 'n/a' }
    | null
    | undefined,
): boolean {
  if (!fill) return false
  if (fill.type === 'image' || fill.type === 'n/a') return false
  if (fill.type !== 'color' && fill.type !== 'paint') return false

  return isTransparent(fill.color)
}

/**
 * Checks if a color is perceptually dark.
 * Uses tinycolor's brightness scale (0–255) with a threshold of 110.
 * Semi-transparent colors (alpha <= 0.5) are not considered dark,
 * since the background bleeds through and lightens the perceived color.
 *
 * @param color - Any color string (hex, rgb, rgba, named, etc.)
 * @returns true if the color is dark and sufficiently opaque, false otherwise
 */
export function isDarkColor(color: string): boolean {
  if (!color) return false
  const tc = tinycolor(color)
  if (!tc.isValid()) return false
  return tc.getBrightness() < 110 && tc.getAlpha() > 0.5
}
